<template>
  <div>
    <header class="fixed bg-white w-screen z-20 top-0 left-0  pointer-events-auto flex p-2">
      <div class="w-1/3 flex items-center">
        <NuxtLink to="/products" class="relative">
          <UButton size="xl" color="neutral" variant="ghost" icon="workbook:line-overview" aria-label="Logout" />
        </NuxtLink>
        <UButton
            icon="workbook:search"
            color="neutral"
            variant="ghost"
            size="lg"
            aria-label="Search"
            @click="searchOpen = true"
          />
      </div>
      <NuxtLink class="w-1/3 flex justify-center" to="/" aria-label="Go to homepage">
        <Logo :isDark="colorMode.value !== 'dark'" class="h-16 w-auto" />
      </NuxtLink>
      <div class="w-1/3 flex items-center justify-end">
        <NuxtLink v-if="cart.totalModels > 0" to="/cart" class="relative">
          <UButton class="cursor-pointer" size="xl" icon="workbook:cart" color="neutral" variant="ghost" aria-label="Open cart" />
            <span v-if="cartReady && cart.totalModels > 0"
              class="absolute top-[6px] right-0 bg-black text-white text-[10px] leading-none px-[6px] py-[4px] rounded-none">
              {{ cart.totalModels }}
            </span>
        </NuxtLink>
        <div class="pt-3">
          <UiBurger :active="menuOpen" @click="toggleMenu" />
        </div>
      </div>
    </header>
    <!-- MEGA MENU-->
    <UiMegaMenuMobile ref="mobileMenu" :menuOpen="menuOpen" @close="menuOpen = false"/>
    <!-- END MEGA-->
    <SearchModal :open="searchOpen" @update:open="searchOpen = $event" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, watch, ref } from 'vue'
import { useCatalogStore } from '@/store/catalog'
import { useCartStore } from '@/store/cart'
import { useAuthStore } from '@/store/auth'
import { useSettingsStore } from '@/store/settings'
import { onClickOutside, useEventListener } from '@vueuse/core'
import ColorModeButton from './ColorModeButton.vue'

const route = useRoute()
const cart = useCartStore()
const auth = useAuthStore()
const settings = useSettingsStore()
const colorMode = useColorMode()
const catalog = useCatalogStore()
const { $logoutFirebase } = useNuxtApp()

const menuOpen = ref(false)
const searchOpen = ref(false)
const mobileMenu = ref<HTMLElement | null>(null)

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value
}

const closeMenu = () => {
  menuOpen.value = false
}

const openMenu = () => {
  if( menuOpen.value ) return
  menuOpen.value = true
}

// Watch to auto-close menu when a menu item is selected (menuOpen set to false)
watch(menuOpen, (val) => {
  if (!val) closeMenu()
})

// onClickOutside(mobileMenu, toggleMenu);

onMounted(() => {
  cart.hydrate()
  settings.hydrate()
  useEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMenu()
    }
  })
})
const cartReady = computed(() => cart.hydrated)

async function logout() {
  await $logoutFirebase()
  auth.clearUser()
  navigateTo('/')
}
function goLogin() {
  navigateTo('/login?redirect=' + encodeURIComponent(route.fullPath))
}

const { data: mainnav } = await useAsyncData('mainnav', () =>
  queryCollection('mainnav').first()
)

const mainMenuItems = computed(() => {
  const menu = Array.isArray(mainnav.value?.menuitems) ? mainnav.value.menuitems : []
  return menu.map((item: any) => ({
    ...item,
    active: item.to
      ? route.path === item.to ||
      (item.to !== '/' && route.path.startsWith(item.to))
      : false
  }))
})
</script>

<style scoped>
@reference "../assets/css/main.css";
.mega-menu {
  @apply w-full min-h-32 bg-white pt-27 pb-32 shadow-2xl pointer-events-none;
  transition: all 0.3s var(--ease-snappy);
  transform: translateY(-100%);
  opacity: 0;
}

.mega-menu.active {
  pointer-events: all;
  opacity: 1;
  transform: translateY(0%);
}
</style>
